import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { prefersReducedMotion } from "./settings";

// Reusable Particle Effects Library — shared across games, celebrations & certificates.
export const PARTICLE_EMOJI = {
  sparkle: "\u2728", heart: "\u2764\uFE0F", confetti: "\u{1F389}", smoke: "\u{1F4A8}",
  tears: "\u{1F4A7}", petals: "\u{1F338}", bubble: "\u{1FAE7}", star: "\u2B50",
  rainbow: "\u{1F308}", leaf: "\u{1F343}", cloud: "\u2601\uFE0F", snow: "\u2744\uFE0F",
};

// A one-shot celebratory burst from the center.
export function ParticleBurst({ type = "sparkle", count = 14, testid }) {
  const rm = prefersReducedMotion();
  const n = rm ? Math.min(5, count) : count;
  const e = PARTICLE_EMOJI[type] || "\u2728";
  const items = useMemo(() => Array.from({ length: n }, (_, i) => ({
    i, angle: (i / n) * Math.PI * 2, dist: 70 + Math.random() * 90, delay: Math.random() * 0.15,
    size: 16 + Math.random() * 14,
  })), [n]);
  return (
    <div className="pointer-events-none absolute inset-0 overflow-visible" aria-hidden="true" data-testid={testid}>
      {items.map((p) => (
        <motion.span key={p.i} className="absolute left-1/2 top-1/2" style={{ fontSize: p.size }}
          initial={{ x: 0, y: 0, opacity: 1, scale: 0.6 }}
          animate={{ x: Math.cos(p.angle) * p.dist, y: Math.sin(p.angle) * p.dist - 30, opacity: 0, scale: 1.2, rotate: Math.random() * 120 - 60 }}
          transition={{ duration: rm ? 0.6 : 1.2, delay: p.delay, ease: "easeOut" }}>{e}</motion.span>
      ))}
    </div>
  );
}

// Gentle ambient drift (background life). Hidden in reduced-motion / calm mode.
export function FloatField({ type = "leaf", count = 6 }) {
  if (prefersReducedMotion()) return null;
  const e = PARTICLE_EMOJI[type] || "\u{1F343}";
  const items = Array.from({ length: count }, (_, i) => ({
    i, left: (i / count) * 100 + Math.random() * 8, delay: Math.random() * 6, dur: 8 + Math.random() * 8,
    size: 14 + Math.random() * 12,
  }));
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      {items.map((p) => (
        <motion.span key={p.i} className="absolute top-[-10%]" style={{ left: `${p.left}%`, fontSize: p.size, opacity: 0.5 }}
          initial={{ y: -40 }} animate={{ y: "110vh", x: [0, 20, -10, 0] }}
          transition={{ duration: p.dur, delay: p.delay, repeat: Infinity, ease: "linear" }}>{e}</motion.span>
      ))}
    </div>
  );
}


// Twinkling sparkle field — pure CSS dots (no emoji font needed), crisp & light.
export function SparkleField({ count = 14, color = "#FFE39A", className = "" }) {
  if (prefersReducedMotion()) return null;
  const items = Array.from({ length: count }, (_, i) => ({
    i, left: Math.random() * 100, top: Math.random() * 100,
    delay: Math.random() * 3, dur: 1.6 + Math.random() * 2.2, size: 3 + Math.random() * 5,
  }));
  return (
    <div className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`} aria-hidden="true">
      {items.map((s) => (
        <motion.span key={s.i} className="absolute rounded-full"
          style={{ left: `${s.left}%`, top: `${s.top}%`, width: s.size, height: s.size, background: color, boxShadow: `0 0 ${s.size * 2}px ${color}` }}
          animate={{ opacity: [0, 1, 0], scale: [0.4, 1, 0.4] }}
          transition={{ duration: s.dur, delay: s.delay, repeat: Infinity, ease: "easeInOut" }} />
      ))}
    </div>
  );
}

// Rising bubbles — soft translucent circles (for underwater / dreamy scenes).
export function BubbleField({ count = 8, color = "rgba(255,255,255,0.5)" }) {
  if (prefersReducedMotion()) return null;
  const items = Array.from({ length: count }, (_, i) => ({
    i, left: Math.random() * 100, delay: Math.random() * 5, dur: 7 + Math.random() * 6, size: 8 + Math.random() * 18,
  }));
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      {items.map((s) => (
        <motion.span key={s.i} className="absolute bottom-[-8%] rounded-full border border-white/40"
          style={{ left: `${s.left}%`, width: s.size, height: s.size, background: color }}
          initial={{ y: 0, opacity: 0 }} animate={{ y: "-115%", x: [0, 8, -6, 0], opacity: [0, 0.7, 0] }}
          transition={{ duration: s.dur, delay: s.delay, repeat: Infinity, ease: "easeInOut" }} />
      ))}
    </div>
  );
}
